'use client';

/**
 * Learning-journey plans — a pricing-section-style row of category cards.
 *
 * <p>Each card is one learning journey laid out like a pricing tier:
 * a colored media header (an authored cover image, or a per-category
 * gradient fallback) with an overlapping icon chip, a clickable title,
 * a short blurb, a clean list of clickable sub-categories, and a CTA
 * button pinned to the bottom so every card aligns. One card can be
 * flagged {@code recommended} — it gets the highlighted treatment
 * (accent ring, "Önerilen" badge, filled CTA) the way the "most
 * popular" plan stands out on a pricing page.
 *
 * <p>Everything is authored from the admin (title, sub-categories,
 * cover image, icon, links, recommended flag) — there is no live feed.
 * Falls back to {@link buildDefaultPlans} so previews always render.
 *
 * <p>The cards render in a horizontal {@link ContentRail} (native scroll
 * + prev/next arrows), capped at {@link LearningJourneyPlansProps.maxItems}
 * (default 5) for the home page, with a "see all" link on the right of the
 * title pointing at {@link LearningJourneyPlansProps.seeAllHref}.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import NextImage from 'next/image';
import { PortalLink, BlockIcon } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { tx, asList, asBool, asNumber } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';

type Localized = string | Record<string, string>;

interface JourneySubCategory {
  title?: Localized;
  href?: string;
}

interface JourneyPlan {
  title?: Localized;
  href?: string;
  /** Cover image shown as the card header (top). */
  coverImage?: string;
  /**
   * Icon — a curated picker name (e.g. {@code "book-open"}), inline
   * {@code <svg>…</svg>} markup, or an image URL. See {@link BlockIcon}.
   */
  icon?: string;
  /** Short blurb under the title. */
  description?: Localized;
  /** Clickable sub-categories listed inside the card. */
  subcategories?: JourneySubCategory[];
  /** When true, the card gets the highlighted "recommended" treatment. */
  recommended?: boolean;
}

export interface LearningJourneyPlansProps {
  kicker?: Localized;
  title?: Localized;
  subtitle?: Localized;
  /** Badge text on the recommended card. */
  recommendedLabel?: Localized;
  /** Label on each card's CTA button (only rendered when the card links). */
  ctaLabel?: Localized;
  /** "See all" link label shown on the right of the title. Defaults to the
   *  catalogue wording. */
  seeAllLabel?: Localized;
  /** Target for the "see all" link. When empty, the link is hidden. */
  seeAllHref?: string;
  /** Max cards to render in the scrollable rail. Default 5. */
  maxItems?: number;
  plans?: JourneyPlan[];
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  /** Draw the decorative gradient rule beside the section heading. */
  headingLine?: boolean;
  paddingInner?: PaddingBox;
}

/**
 * Per-category accent palette. Cards without a cover image render the
 * paired gradient as their header; the solid drives the icon glyph,
 * sub-category chevrons and CTA so each card stays internally cohesive.
 * Indexed by card position so neighbours always differ.
 */
const PALETTE: { grad: string; solid: string }[] = [
  { grad: 'linear-gradient(135deg, #6366f1, #8b5cf6)', solid: '#6366f1' },
  { grad: 'linear-gradient(135deg, #0ea5e9, #2563eb)', solid: '#0284c7' },
  { grad: 'linear-gradient(135deg, #ec4899, #f43f5e)', solid: '#e11d6f' },
  { grad: 'linear-gradient(135deg, #10b981, #059669)', solid: '#059669' },
  { grad: 'linear-gradient(135deg, #f59e0b, #f97316)', solid: '#ea7c0a' },
  { grad: 'linear-gradient(135deg, #14b8a6, #0891b2)', solid: '#0e9aa0' },
];

/** The `t` returned by {@code useTranslation} — typed structurally so this
 *  package needs no direct dependency on i18next's own types. */
type Translate = (key: string) => string;

/** Built per render so a language switch re-reads the catalogue. */
const buildDefaultPlans = (t: Translate): JourneyPlan[] => [
  {
    title: t('journeys.plan.software.title'),
    href: '/journeys/software',
    description: t('journeys.plan.software.description'),
    subcategories: [
      { title: t('journeys.plan.software.item.developer'), href: '/journeys/software/frontend' },
      { title: t('journeys.plan.software.item.frontend'), href: '/journeys/software/frontend' },
      { title: t('journeys.plan.software.item.backend'), href: '/journeys/software/backend' },
      { title: t('journeys.plan.software.item.mobile'), href: '/journeys/software/mobile' },
      { title: t('journeys.plan.software.item.game'), href: '/journeys/software/game' },
      { title: t('journeys.plan.software.item.gis'), href: '/journeys/software/cbs' },
      { title: t('journeys.plan.software.item.blockchain'), href: '/journeys/software/blockchain' },
      { title: t('journeys.plan.software.item.embedded'), href: '/journeys/software/embedded' },
      { title: t('journeys.plan.software.item.rpa'), href: '/journeys/software/rpa' },
    ],
  },
  {
    title: t('journeys.plan.data.title'),
    icon: 'brain',
    href: '/journeys/data',
    recommended: false,
    description: t('journeys.plan.data.description'),
    subcategories: [
      { title: t('journeys.plan.data.item.management'), href: '/journeys/data/analytics' },
      { title: t('journeys.plan.data.item.analyst'), href: '/journeys/data/ml' },
      { title: t('journeys.plan.data.item.ml'), href: '/journeys/data/engineering' },
      { title: t('journeys.plan.data.item.scientist'), href: '/journeys/data/engineering' },
      { title: t('journeys.plan.data.item.bi'), href: '/journeys/data/engineering' },
    ],
  },
  {
    title: t('journeys.plan.design.title'),
    icon: 'palette',
    href: '/journeys/design',
    recommended: true,
    description: t('journeys.plan.design.description'),
    subcategories: [
      { title: t('journeys.plan.design.item.uiux'), href: '/journeys/design/uiux' },
      { title: t('journeys.plan.design.item.product'), href: '/journeys/design/product' },
      { title: t('journeys.plan.design.item.graphic'), href: '/journeys/design/graphic' },
    ],
  },
  {
    title: t('journeys.plan.security.title'),
    icon: 'shield',
    href: '/journeys/security',
    description: t('journeys.plan.security.description'),
    subcategories: [
      {
        title: t('journeys.plan.security.item.specialist'),
        href: '/journeys/security/cybersecurity',
      },
      { title: t('journeys.plan.security.item.pentest'), href: '/journeys/security/pentesting' },
      { title: t('journeys.plan.security.item.forensics'), href: '/journeys/security/forensics' },
      { title: t('journeys.plan.security.item.malware'), href: '/journeys/security/malware' },
    ],
  },
  {
    title: t('journeys.plan.business.title'),
    icon: 'briefcase',
    href: '/journeys/business',
    description: t('journeys.plan.business.description'),
    subcategories: [
      {
        title: t('journeys.plan.business.item.analyst'),
        href: '/journeys/business/business-analysis',
      },
      { title: t('journeys.plan.business.item.testing'), href: '/journeys/business/testing' },
      { title: t('journeys.plan.business.item.devops'), href: '/journeys/business/devops' },
      { title: t('journeys.plan.business.item.pm'), href: '/journeys/business/project-management' },
      { title: t('journeys.plan.business.item.infosec'), href: '/journeys/business/security' },
    ],
  },
];

const CSS = `
  /* "See all" link in the section header (right of the title) */
  .sb-ljp-seeall {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    text-decoration: none;
    color: var(--color-brand-600, #2563eb);
  }
  .sb-ljp-seeall:hover { text-decoration: underline; }
  .sb-ljp-seeall-arrow { transition: transform 160ms ease; }
  .sb-ljp-seeall:hover .sb-ljp-seeall-arrow { transform: translateX(3px); }

  /* Header controls: prev/next buttons sitting next to the "see all" link */
  .sb-ljp-actions {
    display: inline-flex;
    align-items: center;
    gap: 14px;
    align-self: center;
  }
  .sb-ljp-nav { display: inline-flex; align-items: center; gap: 8px; }
  .sb-ljp-navbtn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    padding: 0;
    border-radius: 999px;
    border: 1px solid var(--color-neutral-200, #e4e4e7);
    background: var(--color-surface-card, #fff);
    color: var(--color-text-primary, #18181b);
    cursor: pointer;
    transition: background 150ms ease, border-color 150ms ease, opacity 150ms ease;
  }
  .sb-ljp-navbtn:hover:not(:disabled) {
    background: var(--color-neutral-50, #fafafa);
    border-color: var(--color-neutral-300, #d4d4d8);
  }
  .sb-ljp-navbtn:focus-visible {
    outline: 2px solid var(--color-brand-500, #6366f1);
    outline-offset: 2px;
  }
  .sb-ljp-navbtn:disabled { opacity: 0.4; cursor: default; }
  .sb-ljp-navbtn svg { display: block; }

  /* Horizontal scroller (hidden scrollbar, scroll-snap) */
  .sb-ljp-scroller {
    display: flex;
    gap: clamp(18px, 2.2vw, 26px);
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-block: 2px;
    padding-inline-end: clamp(2rem, 8vw, 5rem);
  }
  .sb-ljp-scroller::-webkit-scrollbar { display: none; width: 0; height: 0; }
  .sb-ljp-item {
    flex: 0 0 auto;
    width: clamp(272px, 82vw, 320px);
    display: flex;
    flex-direction: column;
    scroll-snap-align: start;
  }

  @media (prefers-reduced-motion: reduce) {
    .sb-ljp-scroller { scroll-behavior: auto; }
    .sb-ljp-navbtn { transition: none; }
  }

  /* Rendered as <Card> (with ring-0, so the card keeps its own regular /
     --rec accent border). Elevation comes from <Card>'s
     shadow-(--shadow-brand). overflow:visible is kept explicitly — <Card>
     sets overflow:hidden, which would clip the icon that overhangs the
     media header's bottom edge. */
  .sb-ljp-card {
    --ljp-accent: var(--color-brand-500, #3b82f6);
    position: relative;
    display: flex;
    flex-direction: column;
    /* Fill the rail item so every card in the row matches the tallest. */
    height: 100%;
    overflow: visible;
    background: var(--color-surface-card, #fff);
    border: 1px solid var(--color-neutral-200, #e4e4e7);
    border-radius: var(--radius-xl, 20px);
    transition: transform 220ms ease, border-color 220ms ease;
  }
  .sb-ljp-card:hover {
    transform: translateY(-6px);
    border-color: var(--color-neutral-300, #d4d4d8);
  }
  /* Recommended — the "most popular" treatment. */
  .sb-ljp-card--rec {
    border: 2px solid var(--ljp-accent);
  }

  /* ---- media header ------------------------------------------------- */
  /* No overflow:hidden here — it would clip the icon that overhangs the
     bottom edge. The image + sheen round their own top corners via
     border-radius:inherit instead. */
  .sb-ljp-media {
    position: relative;
    aspect-ratio: 16 / 9;
    border-radius: var(--radius-xl, 20px) var(--radius-xl, 20px) 0 0;
  }
  .sb-ljp-card--rec .sb-ljp-media { border-radius: 18px 18px 0 0; }
  .sb-ljp-media > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: inherit;
  }
  /* subtle top sheen for depth on the gradient fallback */
  .sb-ljp-media::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(120% 90% at 25% 0%, rgba(255, 255, 255, 0.28), transparent 55%);
    pointer-events: none;
    z-index: 1;
  }
  /* on a real photo, swap the sheen for a soft bottom scrim so the
     overlapping icon + any badge stay legible against bright images */
  .sb-ljp-media--photo::after {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 52%, rgba(0, 0, 0, 0.22));
  }
  .sb-ljp-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 11px 5px 9px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.96);
    color: var(--ljp-accent);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.01em;
    z-index: 2;
  }
  .sb-ljp-badge svg { display: block; }
  /* Straddles the header's bottom edge. The media no longer clips
     (overflow:hidden removed), so the overhanging half stays visible. */
  .sb-ljp-icon {
    position: absolute;
    left: 22px;
    bottom: -26px;
    width: 56px;
    height: 56px;
    border-radius: 16px;
    display: grid;
    place-items: center;
    background: var(--color-surface-card, #fff);
    color: var(--ljp-accent);
    z-index: 4;
  }

  /* ---- body --------------------------------------------------------- */
  .sb-ljp-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 40px clamp(20px, 2.2vw, 26px) clamp(20px, 2.2vw, 26px);
    gap: 8px;
  }
  .sb-ljp-title {
    font-size: clamp(17px, 1.7vw, 20px);
    font-weight: 700;
    line-height: 1.25;
    font-family: var(--font-family-display, inherit);
    color: var(--color-text-primary, #18181b);
    text-decoration: none;
    transition: color 160ms ease;
  }
  a.sb-ljp-title:hover { color: var(--ljp-accent); }
  .sb-ljp-desc {
    margin: 0;
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--color-text-secondary, #71717a);
  }
  .sb-ljp-subs {
    list-style: none;
    margin: 14px 0 0;
    padding: 14px 0 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
    border-top: 1px solid var(--color-neutral-200, #e4e4e7);
  }
  .sb-ljp-sub {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 10px;
    margin: 0 -10px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    color: var(--color-text-primary, #3f3f46);
    text-decoration: none;
    transition: background 150ms ease, color 150ms ease;
  }
  a.sb-ljp-sub:hover {
    background: var(--color-neutral-100, #f4f4f5);
    color: var(--ljp-accent);
  }
  /* trailing chevron — only on real links, signalling navigation */
  a.sb-ljp-sub::after {
    content: '';
    width: 7px;
    height: 7px;
    margin-left: auto;
    flex-shrink: 0;
    border-right: 2px solid currentColor;
    border-top: 2px solid currentColor;
    transform: rotate(45deg);
    opacity: 0.35;
    transition: transform 160ms ease, opacity 160ms ease;
  }
  a.sb-ljp-sub:hover::after {
    opacity: 1;
    transform: rotate(45deg) translate(2px, -2px);
  }

  /* ---- CTA ----------------------------------------------------------- */
  .sb-ljp-cta {
    margin-top: auto;
    padding-top: 18px;
  }
  .sb-ljp-cta > a {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 11px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    color: var(--ljp-accent);
    background: transparent;
    border: 1.5px solid var(--color-neutral-200, #e4e4e7);
    transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
  }
  .sb-ljp-cta > a:hover {
    border-color: var(--ljp-accent);
    background: var(--color-neutral-50, #fafafa);
  }
  .sb-ljp-card--rec .sb-ljp-cta > a {
    color: #fff;
    background: var(--ljp-accent);
    border-color: var(--ljp-accent);
  }
  .sb-ljp-card--rec .sb-ljp-cta > a:hover { filter: brightness(0.94); }
  .sb-ljp-cta-arrow { transition: transform 160ms ease; }
  .sb-ljp-cta > a:hover .sb-ljp-cta-arrow { transform: translateX(3px); }

  @media (prefers-reduced-motion: reduce) {
    .sb-ljp-card, .sb-ljp-title, .sb-ljp-sub, a.sb-ljp-sub::after,
    .sb-ljp-cta > a, .sb-ljp-cta-arrow { transition: none; }
    .sb-ljp-card:hover { transform: none; }
    a.sb-ljp-sub:hover::after { transform: rotate(45deg); }
    .sb-ljp-cta > a:hover .sb-ljp-cta-arrow { transform: none; }
  }
`;

function StarGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.9 6.19 20.9l1.11-6.47-4.7-4.58 6.5-.95z" />
    </svg>
  );
}

function PlanCard({
  plan,
  index,
  recommendedLabel,
  ctaLabel: _ctaLabel,
}: {
  plan: JourneyPlan;
  index: number;
  recommendedLabel: string;
  ctaLabel: string;
}) {
  const isRec = asBool(plan.recommended);
  const accent = PALETTE[index % PALETTE.length]!;
  const titleText = tx(plan.title);
  const subs = asList<JourneySubCategory>(plan.subcategories);

  const cardStyle = { '--ljp-accent': accent.solid } as CSSProperties;
  // Gradient always sits behind the image so a slow/broken cover still
  // renders as a tasteful colored header instead of a gap.
  const mediaStyle: CSSProperties = {
    backgroundImage: accent.grad,
    backgroundColor: accent.solid,
  };
  const mediaClass = plan.coverImage ? 'sb-ljp-media sb-ljp-media--photo' : 'sb-ljp-media';

  const titleNode = plan.href ? (
    <span className="sb-ljp-title">{titleText}</span>
  ) : (
    // <PortalLink href={plan.href} className="sb-ljp-title">
    // {titleText}
    // {/* </PortalLink> */}
    <span className="sb-ljp-title">{titleText}</span>
  );

  return (
    <Card asChild className="ring-0">
      <article className={isRec ? 'sb-ljp-card sb-ljp-card--rec' : 'sb-ljp-card'} style={cardStyle}>
        <div className={mediaClass} style={mediaStyle}>
          {plan.coverImage && (
            <NextImage
              src={plan.coverImage}
              alt=""
              fill
              sizes="(max-width: 640px) 100vw, 400px"
              style={{ objectFit: 'cover', borderRadius: 'inherit' }}
              loading="lazy"
            />
          )}
          {isRec && recommendedLabel && (
            <span className="sb-ljp-badge">
              <StarGlyph />
              {recommendedLabel}
            </span>
          )}
          {plan.icon && (
            <div className="sb-ljp-icon">
              <BlockIcon value={plan.icon} size={26} />
            </div>
          )}
        </div>

        <div className="sb-ljp-body">
          {titleNode}
          {plan.description && <p className="sb-ljp-desc">{tx(plan.description)}</p>}

          {subs.length > 0 && (
            <ul className="sb-ljp-subs">
              {subs.map((s, i) => {
                const label = tx(s.title);
                return (
                  <li key={i}>
                    {s.href ? (
                      <PortalLink href={s.href} className="sb-ljp-sub">
                        {label}
                      </PortalLink>
                    ) : (
                      <span className="sb-ljp-sub">{label}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* {plan.href && ctaLabel && (
          <div className="sb-ljp-cta">
            <PortalLink href={plan.href}>
              {ctaLabel}
              <span className="sb-ljp-cta-arrow" aria-hidden="true">
                →
              </span>
            </PortalLink>
          </div>
        )} */}
        </div>
      </article>
    </Card>
  );
}

export function LearningJourneyPlans({
  kicker,
  title,
  subtitle,
  recommendedLabel,
  ctaLabel,
  seeAllLabel,
  seeAllHref = '/journeys',
  maxItems = 5,
  plans,
  style,
  paddingOuter,
  headingLine,
  paddingInner,
}: LearningJourneyPlansProps) {
  const { t } = useTranslation('site-builder-blocks');
  const raw = asList<JourneyPlan>(plans);
  const full = raw.length > 0 ? raw : buildDefaultPlans(t);
  // Home page shows a capped, scrollable rail; "see all" links to the rest.
  const cap = Math.max(1, asNumber(maxItems, 5));
  const list = full.slice(0, cap);
  const recLabel = tx(recommendedLabel) || t('journeys.recommended');
  const cta = tx(ctaLabel) || t('journeys.cta');
  const titleText = tx(title) || t('journeys.title');
  const subtitleText = tx(subtitle) || t('journeys.subtitle');
  const seeAllText = tx(seeAllLabel) || t('journeys.seeAll');
  const seeAll = (seeAllHref ?? '').trim();

  // ── Horizontal scroller — driven by the prev/next buttons in the header ──
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, list.length]);

  const scrollByPage = useCallback((dir: 'left' | 'right') => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  const hasOverflow = canLeft || canRight;

  // Header action: prev/next nav buttons (when scrollable) + the see-all link.
  const actionNode =
    hasOverflow || seeAll ? (
      <div className="sb-ljp-actions">
        {hasOverflow && (
          <div className="sb-ljp-nav">
            <button
              type="button"
              className="sb-ljp-navbtn"
              aria-label={t('journeys.scrollBack')}
              disabled={!canLeft}
              onClick={() => scrollByPage('left')}
            >
              <ChevronLeftGlyph />
            </button>
            <button
              type="button"
              className="sb-ljp-navbtn"
              aria-label={t('journeys.scrollForward')}
              disabled={!canRight}
              onClick={() => scrollByPage('right')}
            >
              <ChevronRightGlyph />
            </button>
          </div>
        )}
        {seeAll && seeAllText && (
          <PortalLink href={seeAll} className="sb-ljp-seeall">
            {seeAllText}
            <span className="sb-ljp-seeall-arrow" aria-hidden="true">
              →
            </span>
          </PortalLink>
        )}
      </div>
    ) : undefined;

  return (
    <SectionShell
      headingLine={headingLine}
      style={style}
      kicker={tx(kicker)}
      title={titleText}
      subtitle={subtitleText}
      action={actionNode}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div ref={scrollerRef} className="sb-ljp-scroller" role="region" aria-label={titleText}>
        {list.map((plan, i) => (
          <div key={i} className="sb-ljp-item">
            <PlanCard plan={plan} index={i} recommendedLabel={recLabel} ctaLabel={cta} />
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function ChevronLeftGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
